/**
 * 客户端鉴权状态 (token / 当前用户), 持久化到 localStorage。
 * 服务端的"当前用户详情"通过 TanStack Query (useCurrentUser) 拉, 不放这里。
 */
import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  browserStorage,
  isLegacyToken,
  readLegacySession,
  removeLegacySession,
  syncLegacyAccessToken,
  writeLegacySession,
} from "./legacy-session";

export interface AuthUser {
  id: number;
  username: string;
  display_name?: string;
  role: "user" | "company_admin" | "super_admin";
  company_id: number | null;
  company_name?: string | null;
  // 以下字段来自 /api/me (登录响应里没有), 由 useCurrentUser 合并进来
  company_type?: "org" | "personal" | null;
  phone?: string | null;
  email?: string | null;
  credits_enabled?: boolean;
  credits_fen?: number;
  /** 余额 (元) */
  credits?: number;
}

interface AuthState {
  token: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  setSession: (token: string, refreshToken: string, user: AuthUser) => void;
  /** refresh-token 换到新 access token 后只替换它 */
  setAccessToken: (token: string) => void;
  /** 合并 /api/me 的最新字段 */
  patchUser: (patch: Partial<AuthUser>) => void;
  /**
   * 清空会话。旧版 key 是同一个会话时一并删除;
   * logout=true (用户主动退出) 时无论如何都删旧版 key —— 退出即两边都退出。
   */
  clear: (opts?: { logout?: boolean }) => void;
  isAdmin: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      refreshToken: null,
      user: null,
      setSession: (token, refreshToken, user) => {
        // 同步写旧版 key: 普通用户登录后默认进旧版 /static/, 那边要直接处于登录状态
        const storage = browserStorage();
        if (storage) writeLegacySession(storage, token, refreshToken, user);
        set({ token, refreshToken, user });
      },
      setAccessToken: (token) => {
        const storage = browserStorage();
        if (storage) syncLegacyAccessToken(storage, get().token, token);
        set({ token });
      },
      patchUser: (patch) => {
        const u = get().user;
        if (u) set({ user: { ...u, ...patch } });
      },
      clear: (opts) => {
        // 同一会话 (React 写的, 或 React 从旧版接力来的): 这个会话已失效 / 已退出, 旧版 key 一起删,
        // 否则旧页面拿着死 token, 刷新 React 又会被它接力回来。
        // 旧版是另一次登录 (在 login.html 重新登录过) 且只是被动清 (401): 保留, 刷新后 React 接力它。
        const storage = browserStorage();
        if (storage && (opts?.logout || isLegacyToken(storage, get().token))) {
          removeLegacySession(storage);
        }
        set({ token: null, refreshToken: null, user: null });
      },
      isAdmin: () => {
        const u = get().user;
        return u !== null && (u.role === "super_admin" || u.role === "company_admin");
      },
    }),
    {
      name: "auth_store_v1",
      partialize: (state) => ({
        token: state.token,
        refreshToken: state.refreshToken,
        user: state.user,
      }),
    },
  ),
);

/**
 * React 没有会话 (auth_store_v1 为空) 而旧版页面已登录 (旧版 login.html 登录) 时,
 * 直接沿用旧版登录态, 从 /static/* 跳到 /admin/ 不用重新登录 (见 legacy-session.ts)。
 * persist 用的是同步的 localStorage, 到这里已经水合完毕。
 */
function bootstrapFromLegacySession() {
  const storage = browserStorage();
  if (!storage || useAuthStore.getState().token) return;
  const legacy = readLegacySession(storage);
  if (!legacy) return;
  useAuthStore.setState({ token: legacy.token, refreshToken: legacy.refreshToken, user: legacy.user });
}

bootstrapFromLegacySession();
